import {
  MdxCallout,
  MdxTabs,
  MdxTab,
  MdxAccordion,
  MdxAccordionGroup,
  MdxCard,
  MdxCardGroup,
  MdxSteps,
  MdxStep,
  MdxMdxHeading,
  MdxMdxLink,
  MdxMdxImage,
  MdxMdxCodeBlock,
  MdxMdxInlineCode,
  MdxMdxTable,
  MdxMdxTableHead,
  MdxMdxTableRow,
  MdxMdxTableHeader,
  MdxMdxTableData,
  MdxStickyTable,
  MdxTooltip,
  MdxEndpointRequestSnippet,
  MdxEndpointResponseSnippet,
  MdxBanner,
  MdxProductShowcase,
  MdxNextStepBlock,
} from '@squareup/dex-feature-mdx-adapters';
import type { ComponentType } from 'react';

type MdxComponentMap = Record<string, ComponentType<Record<string, unknown>>>;

function getMdxComponentMap(): MdxComponentMap {
  return {
    // Fern callout components → Aside adapters
    Info: MdxCallout.Info,
    Warning: MdxCallout.Warning,
    Tip: MdxCallout.Tip,
    Note: MdxCallout.Note,
    Success: MdxCallout.Success,
    // Fern's <Important> is a callout variant. Map onto Warning per Fern's
    // own visual convention (red-bordered emphasis box, not the gentler
    // Note). MDXRemote would otherwise render `<Important>` as an
    // unrecognized component and the page would fail.
    Important: MdxCallout.Warning,

    // Fern layout components
    Tabs: MdxTabs,
    Tab: MdxTab,
    Accordion: MdxAccordion,
    AccordionGroup: MdxAccordionGroup,
    Card: MdxCard,
    CardGroup: MdxCardGroup,
    Steps: MdxSteps,
    Step: MdxStep,
    Tooltip: MdxTooltip,

    // HTML element overrides
    h1: MdxMdxHeading.H1,
    h2: MdxMdxHeading.H2,
    h3: MdxMdxHeading.H3,
    h4: MdxMdxHeading.H4,
    a: MdxMdxLink,
    img: MdxMdxImage,
    pre: MdxMdxCodeBlock,
    code: MdxMdxInlineCode,
    table: MdxMdxTable,
    thead: MdxMdxTableHead,
    tr: MdxMdxTableRow,
    th: MdxMdxTableHeader,
    td: MdxMdxTableData,

    // PascalCase aliases for JSX-literal use. MDX compiles lowercase
    // JSX (`<table>`) as native HTML elements, bypassing the lowercase
    // entries above, so authors who need raw HTML attributes (colSpan,
    // rowSpan, sticky, …) must use the PascalCase form to get the
    // dex-ui Table styling. Markdown tables continue to flow through
    // the lowercase entries.
    Table: MdxMdxTable,
    THead: MdxMdxTableHead,
    TR: MdxMdxTableRow,
    TH: MdxMdxTableHeader,
    TD: MdxMdxTableData,

    // Fern's <StickyTable> wraps a markdown table with sticky-header
    // presentation. The adapter is a context Provider — the inner
    // markdown <table> reads it and flips dex-ui's `hasStickyHeader`.
    StickyTable: MdxStickyTable,

    // Fern's <EndpointRequestSnippet api="latest" endpoint="GET /v2/foo"
    // example="..."> renders a multi-language API code widget. The
    // adapter resolves the endpoint via the OAS graph and delegates to
    // MarkdownIgnitionCodeBlock (same widget Contentful pages use).
    EndpointRequestSnippet: MdxEndpointRequestSnippet,

    // Fern's <EndpointResponseSnippet api="latest" endpoint="GET /v2/foo"
    // example="..."> renders the example RESPONSE body for that endpoint
    // as a JSON code block. Same scenario fallback chain as the Request
    // variant (named → first → endpoint default).
    EndpointResponseSnippet: MdxEndpointResponseSnippet,

    // Fern's `<Banner imageSrc=… darkImageSrc=… imageAlt=…>body</Banner>`
    // hero, used by the marquee landing pages (commerce, customers,
    // merchant-details, payments-overview, staff). The adapter wraps
    // dex-ui's existing DocPageBanner — same chrome the Contentful
    // MarkdownBanner uses — with a reactive light/dark image swap.
    Banner: MdxBanner,

    // Fern's <ProductShowcase image={…} linkText="…" linkUrl="…">…</> is
    // an image+text+CTA layout. Several overview pages embed it inside
    // <Tabs> to swap product variants. Empty linkText/linkUrl values are
    // valid and skip the CTA (see in-person-payment-options.mdx).
    ProductShowcase: MdxProductShowcase,

    // Contentful stores previous/next navigation as a `next-steps` block
    // outside the markdown body. Use an explicit MDX component for pages that
    // need to preserve that footer during migration.
    NextStepBlock: MdxNextStepBlock,
  } as unknown as MdxComponentMap;
}

export { getMdxComponentMap };
export type { MdxComponentMap };
